import { useFormContext } from 'react-hook-form';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistoryDemographicsFormValues, IHistoryDemographicsModel } from '@/types/history/history-demographics';
import FieldError from '@components/field-error';

interface IIdentificationSectionProps {
    model: IHistoryDemographicsModel;
    t: TLocalize;
}

const IdentificationSection = ({ model, t }: IIdentificationSectionProps) => {
    const {
        register,
        watch,
        formState: { errors },
    } = useFormContext<IHistoryDemographicsFormValues>();

    const isUsIssuer = watch('dlIssueCountry') === String(model.usCountryValue);
    const identificationTab = watch('identificationTab');

    return (
        <>
            <div className='section-heading'>
                {t(
                    'DashLabel_PleaseEnterOneOfTheFollowingIdentificationGroups',
                    'Identification',
                )}
            </div>

            <div className='form-group' style={{ marginBottom: 0 }}>
                <div className='form-group'>
                    <label className="control-label col-xs-12">
                        {t(
                            'DashLabel_IdentificationType',
                            'Type of Identification',
                        )}
                    </label>
                    <div className="col-xs-12" style={{ marginLeft: 10 }}>
                        <label className="radio-inline">
                            <input
                                type="radio"
                                value="ssn"
                                {...register('identificationTab')}
                            />{' '}
                            {t('Word_SSN', 'SSN')}
                        </label>
                        <label className="radio-inline">
                            <input
                                type="radio"
                                value="dl"
                                {...register('identificationTab')}
                            />{' '}
                            {t('DashLabel_GovtIssuedId', 'Govt. Issued ID')}
                        </label>
                    </div>
                </div>
            </div>

            {identificationTab === 'ssn' && (
                <div className="form-group">
                    <label className="control-label col-xs-12">
                        SSN #:
                        <FieldError message={errors.ssn?.message} />
                    </label>
                    <div className="col-xs-12 col-md-8">
                        <input
                            className="form-control"
                            autoComplete="off"
                            maxLength={9}
                            placeholder={t('DashLabel_NumbericOnly', 'numeric only')}
                            {...register('ssn')}
                        />
                    </div>
                </div>
            )}

            {identificationTab === 'dl' && (
                <>
                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('DashLabel_IssuingCountry', 'Issuing Country')}:
                            <FieldError message={errors.dlIssueCountry?.message} />
                        </label>
                        <div className="col-xs-12 col-md-8">
                            <select className="form-control" {...register('dlIssueCountry')}>
                                {model.countryOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {isUsIssuer ? (
                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                {t('DashLabel_IssuingState', 'Issuing State')}:
                                <FieldError message={errors.dlIssueState?.message} />
                            </label>
                            <div className="col-xs-12 col-md-8">
                                <select className="form-control" {...register('dlIssueState')}>
                                    <option value="">
                                        {t('DashLabel_PleaseSelect', 'Please Select')}
                                    </option>
                                    {model.stateOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className="form-group">
                            <label className="control-label col-xs-12">
                                {t('DashLabel_IssuingStateProvince', 'Issuing State/Province')}:
                                <FieldError message={errors.dlNonUsStateProvince?.message} />
                            </label>
                            <div className="col-xs-12 col-md-8">
                                <input className="form-control" {...register('dlNonUsStateProvince')} />
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('Word_Identification', 'Identification')} #:
                            <FieldError message={errors.dlNumber?.message} />
                        </label>
                        <div className="col-xs-12 col-md-8">
                            <input
                                className="form-control"
                                autoComplete="off"
                                maxLength={255}
                                {...register('dlNumber')}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t("DashLabel_MotherSMaidenName", "Mother's Maiden Name")}:
                            <FieldError message={errors.mothersMaiden?.message} />
                        </label>
                        <div className="col-xs-12 col-md-8">
                            <input
                                className="form-control"
                                autoComplete="off"
                                {...register('mothersMaiden')}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default IdentificationSection;
